import { useRef, useState, useCallback, useEffect } from "react";

interface VideoUploadFieldProps {
	onFileSelect: (file: File | null) => void;
	disabled?: boolean;
	className?: string;
}

const MAX_FILE_SIZE_MB = 500;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo"];
const ALLOWED_EXTENSIONS = [".mp4", ".webm", ".mov", ".avi"];

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function validateFile(file: File): string | null {
	// Check file type
	const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
	const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

	if (!isValidType) {
		return `Invalid file type. Please upload MP4, WebM, or MOV files.`;
	}

	// Check file size
	if (file.size > MAX_FILE_SIZE_BYTES) {
		return `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Your file is ${formatFileSize(file.size)}.`;
	}

	return null;
}

export default function VideoUploadField({
	onFileSelect,
	disabled = false,
	className = "",
}: VideoUploadFieldProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const videoRef = useRef<HTMLVideoElement>(null);

	const [error, setError] = useState<string | null>(null);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [videoDuration, setVideoDuration] = useState<number | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);

	// Cleanup preview URL on unmount
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const handleFileSelect = useCallback((file: File) => {
		// Validate file
		const validationError = validateFile(file);
		if (validationError) {
			setError(validationError);
			onFileSelect(null);
			return;
		}

		setSelectedFile(file);
		setError(null);

		// Create preview URL
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);

		// Notify parent
		onFileSelect(file);
	}, [onFileSelect, previewUrl]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleClick = () => {
		if (disabled) return;
		inputRef.current?.click();
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (disabled) return;
		setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		if (disabled) return;

		const file = e.dataTransfer.files[0];
		if (file) {
			handleFileSelect(file);
		}
	};

	const handleRemove = () => {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		setSelectedFile(null);
		setPreviewUrl(null);
		setVideoDuration(null);
		setError(null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
		onFileSelect(null);
	};

	const handleVideoLoadedMetadata = () => {
		if (videoRef.current) {
			setVideoDuration(videoRef.current.duration);
		}
	};

	// Idle state - show upload zone
	if (!selectedFile) {
		return (
			<div className={className}>
				<input
					ref={inputRef}
					type="file"
					accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
					onChange={handleInputChange}
					className="hidden"
					disabled={disabled}
				/>
				<div
					onClick={handleClick}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer group ${
						isDragOver
							? "border-brand bg-brand/5"
							: disabled
								? "border-gray-200 bg-gray-50 cursor-not-allowed"
								: "border-gray-200 hover:border-brand/50"
					}`}
				>
					<svg
						className={`w-10 h-10 mx-auto transition-colors ${
							isDragOver ? "text-brand" : "text-gray-300 group-hover:text-brand/80"
						}`}
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
						/>
					</svg>
					<p className="text-sm text-tertiary mt-2">
						{isDragOver ? "Drop video here" : "Click to upload or drag and drop"}
					</p>
					<p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV up to {MAX_FILE_SIZE_MB}MB</p>
				</div>
				{error && (
					<p className="text-xs text-red-500 mt-2">{error}</p>
				)}
			</div>
		);
	}

	// File selected - show preview
	return (
		<div className={className}>
			<div className="border border-gray-200 rounded-xl overflow-hidden">
				<div className="relative">
					{/* Video preview */}
					{previewUrl && (
						<video
							ref={videoRef}
							src={previewUrl}
							className="w-full aspect-video object-cover"
							controls
							onLoadedMetadata={handleVideoLoadedMetadata}
						/>
					)}
				</div>

				<div className="p-4 bg-gray-50 border-t border-gray-200">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
								<svg className="w-4 h-4 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<div>
								<p className="text-sm font-medium text-primary truncate max-w-[200px]">
									{selectedFile.name}
								</p>
								<p className="text-xs text-tertiary">
									{formatFileSize(selectedFile.size)}
									{videoDuration && ` · ${formatDuration(videoDuration)}`}
								</p>
							</div>
						</div>

						<button
							type="button"
							onClick={handleRemove}
							disabled={disabled}
							className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
							title="Remove video"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
